// frontend/src/pages/admin/lcl/tabs/MandatesTab.jsx
import { useMemo, useState } from 'react';
import { FiSearch, FiDownload, FiRefreshCw, FiAlertTriangle, FiCheckCircle } from 'react-icons/fi';
import { useLclMandates } from '../hooks/useLclMandates';
import IcsBadge from '../components/IcsBadge';
import MaskedIban from '../components/MaskedIban';
import ExportConfirmModal from '../components/ExportConfirmModal';
import RibCorrectionModal from '../components/RibCorrectionModal';
import PeriodFilter, { inPeriod } from '../components/PeriodFilter';

const fmtDate = (d) => (d ? new Date(d).toLocaleDateString('fr-FR') : '—');

export default function MandatesTab() {
  const [search, setSearch]     = useState('');
  const [ics, setIcs]           = useState('all');
  const [exported, setExported] = useState('false');
  const [period, setPeriod]     = useState({ mode: 'year', value: String(new Date().getFullYear()) });
  const [confirm, setConfirm]   = useState(null);
  const [editRib, setEditRib]   = useState(null);

  const { data, loading, refetch, exportCsv } = useLclMandates({ ics_status: ics, exported, search });

  const filtered = useMemo(
    () => data.filter((d) => inPeriod(d.signed_at, period)),
    [data, period]
  );

  const counts = useMemo(() => ({
    new: filtered.filter((d) => d.ics_status === 'new' && !d.lcl_exported_at).length,
    old: filtered.filter((d) => d.ics_status === 'old' && !d.lcl_exported_at).length,
  }), [filtered]);

  return (
    <div className="space-y-3">

      {/* KPIs ICS */}
      <div className="grid grid-cols-3 gap-2">
        <div className="bg-white border border-gray-200 rounded p-2">
          <div className="text-[10px] text-gray-500 font-medium">Mandats nouveau ICS</div>
          <div className="text-base font-bold text-green-700">{counts.new}</div>
        </div>
        <div className="bg-white border border-gray-200 rounded p-2">
          <div className="text-[10px] text-gray-500 font-medium">Mandats ancien ICS</div>
          <div className="text-base font-bold text-red-700">{counts.old}</div>
        </div>
        <div className="bg-white border border-gray-200 rounded p-2">
          <div className="text-[10px] text-gray-500 font-medium">Total filtré</div>
          <div className="text-base font-bold text-blue-700">{filtered.length}</div>
        </div>
      </div>

      {/* Bandeau export */}
      <div className="bg-white rounded shadow border border-gray-200 px-3 py-2 flex items-center justify-between gap-2 flex-wrap">
        <div className="text-[10px] text-gray-500">
          Exporter les mandats vers le portail LCL. Un mandat ne peut être exporté qu'une seule fois.
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => setConfirm('new')}
            disabled={counts.new === 0}
            className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded text-[11px] font-semibold text-white
                       bg-green-600 hover:bg-green-700 disabled:bg-gray-200 disabled:text-gray-400 disabled:cursor-not-allowed"
          >
            <FiCheckCircle className="h-3.5 w-3.5" />
            CSV Nouveau ({counts.new})
          </button>
          <button
            onClick={() => setConfirm('old')}
            disabled={counts.old === 0}
            className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded text-[11px] font-semibold text-white
                       bg-red-600 hover:bg-red-700 disabled:bg-gray-200 disabled:text-gray-400 disabled:cursor-not-allowed"
          >
            <FiAlertTriangle className="h-3.5 w-3.5" />
            CSV Ancien ({counts.old})
          </button>
        </div>
      </div>

      {/* Filtres */}
      <div className="bg-white rounded border border-gray-200 p-2 flex flex-wrap items-center gap-2">

        <div className="relative flex-1 min-w-[180px]">
          <FiSearch className="absolute left-2 top-1/2 -translate-y-1/2 text-gray-400 h-3.5 w-3.5" />
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Nom client, SIREN, RUM…"
            className="w-full pl-7 pr-2 py-1 text-[11px] border border-gray-200 rounded
                       focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
          />
        </div>

        <div className="inline-flex rounded border border-gray-200 overflow-hidden">
          {[
            { k: 'all', l: 'Tous ICS' },
            { k: 'new', l: 'Nouveau' },
            { k: 'old', l: 'Ancien' },
          ].map(({ k, l }) => (
            <button
              key={k}
              onClick={() => setIcs(k)}
              className={`px-2.5 py-1 text-[11px] font-medium transition-colors ${
                ics === k ? 'bg-blue-600 text-white' : 'bg-white text-gray-600 hover:bg-gray-50'
              }`}
            >
              {l}
            </button>
          ))}
        </div>

        <div className="inline-flex rounded border border-gray-200 overflow-hidden">
          {[
            { k: 'false', l: 'Non exportés' },
            { k: 'true', l: 'Exportés' },
            { k: '', l: 'Tous' },
          ].map(({ k, l }) => (
            <button
              key={k}
              onClick={() => setExported(k)}
              className={`px-2.5 py-1 text-[11px] font-medium transition-colors ${
                exported === k ? 'bg-blue-600 text-white' : 'bg-white text-gray-600 hover:bg-gray-50'
              }`}
            >
              {l}
            </button>
          ))}
        </div>

        <PeriodFilter period={period} setPeriod={setPeriod} />

        <button
          onClick={refetch}
          className="inline-flex items-center gap-1 px-2 py-1 text-[11px] rounded border border-gray-200 hover:bg-gray-50 text-gray-600"
        >
          <FiRefreshCw className={`h-3.5 w-3.5 ${loading ? 'animate-spin' : ''}`} />
          Actualiser
        </button>
      </div>

      {/* Tableau */}
      <div className="bg-white rounded shadow border border-gray-200 overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center py-8 gap-2 text-xs text-gray-400">
            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-500" />
            Chargement…
          </div>
        ) : filtered.length === 0 ? (
          <div className="text-center py-8 text-xs text-gray-400">
            Aucun mandat pour cette sélection.
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-100">
              <thead className="bg-gray-50">
                <tr>
                  {['ICS', 'Client', 'SIREN', 'RUM', 'Signé le', 'IBAN', 'Ville', 'Exporté', 'Action']
                    .map((h) => (
                      <th key={h}
                          className="px-3 py-2 text-left text-[10px] font-semibold
                                     text-gray-500 uppercase tracking-wider whitespace-nowrap">
                        {h}
                      </th>
                    ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {filtered.map((d) => (
                  <tr key={d.id} className="hover:bg-gray-50">
                    <td className="px-3 py-2 whitespace-nowrap">
                      <IcsBadge status={d.ics_status} />
                    </td>
                    <td className="px-3 py-2 whitespace-nowrap">
                      <div className="text-[11px] font-semibold text-gray-900">
                        {d.accounts?.company_name || '—'}
                      </div>
                    </td>
                    <td className="px-3 py-2 whitespace-nowrap text-[11px] text-gray-500 font-mono">
                      {d.accounts?.siren || '—'}
                    </td>
                    <td className="px-3 py-2 whitespace-nowrap text-[11px] text-gray-500 font-mono">
                      {d.metadata?.rum || '—'}
                    </td>
                    <td className="px-3 py-2 whitespace-nowrap text-[11px] text-gray-500">
                      {fmtDate(d.signed_at)}
                    </td>
                    <td className="px-3 py-2 whitespace-nowrap text-[11px]">
                      <MaskedIban iban={d.accounts?.iban} />
                    </td>
                    <td className="px-3 py-2 whitespace-nowrap text-[11px] text-gray-500">
                      {d.accounts?.city || '—'}
                    </td>
                    <td className="px-3 py-2 whitespace-nowrap">
                      {d.lcl_exported_at ? (
                        <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-semibold bg-gray-100 text-gray-700">
                          {fmtDate(d.lcl_exported_at)}
                        </span>
                      ) : (
                        <span className="text-[10px] text-gray-400">—</span>
                      )}
                    </td>
                    <td className="px-3 py-2 whitespace-nowrap">
                      <button
                        onClick={() => setEditRib(d.accounts)}
                        className="text-[10px] px-2 py-0.5 rounded border border-gray-300 hover:bg-blue-50 hover:border-blue-300 text-blue-700 font-semibold"
                      >
                        Corriger RIB
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <ExportConfirmModal
        open={!!confirm}
        onClose={() => setConfirm(null)}
        onConfirm={async () => { const s = confirm; setConfirm(null); await exportCsv(s); }}
        title={`Exporter mandats — ${confirm === 'new' ? 'Nouveau' : 'Ancien'} portail LCL`}
        description={
          <>
            <div className="mb-1">
              <strong className={confirm === 'new' ? 'text-green-700' : 'text-red-700'}>
                {confirm === 'new' ? counts.new : counts.old} mandats
              </strong>
              {' '}seront inclus dans le fichier CSV.
            </div>
            <div className="text-gray-500 text-[10px]">
              Ils ne pourront plus être réexportés après validation.
            </div>
          </>
        }
        confirmLabel="Exporter"
        confirmColor={confirm === 'new' ? 'green' : 'orange'}
      />

      <RibCorrectionModal
        open={!!editRib}
        account={editRib}
        onClose={() => setEditRib(null)}
        onSaved={() => { setEditRib(null); refetch(); }}
      />
    </div>
  );
}
